/**
 * Component for our main Return-Dashboard that displays
 * all of the user's Returns.
 *
 * Also includes the initial onboarding screen that is
 * shown on first use of the App.
 *
 * @component
 */

import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Dropdown,
  Input,Popconfirm,
  Row,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'

import {
  SmileOutlined,
  VideoCameraOutlined,
  WarningOutlined,
  DownOutlined,
  ReloadOutlined,
  UpOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import React from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Helmet } from 'react-helmet'
import VideoPopup from '../../../components/VideoPopup'
import { SubscriptionContext } from '../../../contexts/SubscriptionContext'
import { getAppInfo } from '../../../helpers/appInfo'
import LoginModal from './components/LoginModal'
import ManualReturn from './components/ManualReturn'
import ReturnsTable from './components/ReturnsTable'
import './ReturnStyles.css'
import { trackingVitally } from '../../../helpers/vitally'
import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

dayjs.extend(quarterOfYear)

const { Search } = Input
const { Step } = Steps
const { Title } = Typography
const { Text } = Typography
const { RangePicker } = DatePicker
const items = [
  {
    key: '1',
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.antgroup.com">
        1st menu item
      </a>
    ),
  },
  {
    key: '2',
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.aliyun.com">
        2nd menu item (disabled)
      </a>
    ),
    icon: <SmileOutlined />,
    disabled: true,
  },
  {
    key: '3',
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.luohanacademy.com">
        3rd menu item (disabled)
      </a>
    ),
    disabled: true,
  },
  {
    key: '4',
    danger: true,
    label: 'a danger item',
  },
]

class Returns extends React.Component {
  static contextType = SubscriptionContext

  constructor(props) {
    super(props)
    this.state = {
      returnsCount: undefined,
      returns: [],
      productsOfReturns: [],
      returnsEmpty: true,
      resolutionLabel: undefined,
      buttonResetSearchVisible: false,
      selectedRowKeys: [],
      selectedRows: [],
      loading: false,
      searchValue: '',
      isLoading: false,
      pageCurrent: 1,
      appUrl: null,
      isProductionApp: null,
      env: null,
      showFilters: false,
      startDate: null,
      endDate: null,
      sortedBy: { key: 'createdAt',direction: 'DESC' },
      visibleCustomize: false,
      rangeApplied: false,
      visibleStatus: false,
      visibleType: false,
      visibleReslotions: false,
      visibleReturnReasons: false,
      visibleReturnCountry: false,
      visibleReturnMethod: false,
      visibleShippingCarrier: false,
      showExtraFilters: false,
      allowedStatus: {
        All: 'All',
        Pending: 'Pending',
        Approved: 'Approved',
        Shipped: 'Shipped',
        InTransit: 'In Transit',
        Received: 'Received',
        Resolved: 'Resolved',
        Rejected: 'Rejected',
      },
      allowedColumns: {
        Return: 'Return',
        Status: 'Status',
        Journey: 'Journey',
        Label: 'Label',
        Type: 'Type',
        Order: 'Order',
        Customer: 'Customer',
        Resolution: 'Resolution',
        'Return Reason': 'Return Reason',
        'Return Method': 'Return Method',
        'Order Country': 'Order Country',
        'Return Cost': 'Return Cost',
        'Shipping Carrier': 'Shipping Carrier',
        'Request date': 'Request date',
        Action: 'Action'
      },
      filters: {},
      tags: {},
      tagsArray: [],
      searchTerm: '',
      showModal: false,
      hasSubmittedIndustry: false,
      loadingRecreateLabel: false,
      loading: true,
      loadingTableData: false,
      returnCountries: {},
      type: {
        return: 'Return',
        giftReturn: 'Gift',
        warranty: 'Warranty',
      },
      returnMethods: {
        methodLabelCreation: 'Prepaid Label (Auto)',
        methodLabelManual: 'Prepaid Label (Manual)',
        methodCustomerPaidLabel: 'Customer Paid Label',
        methodCustomerChoice: 'Customer Responsible',
        methodInStore: 'In-Store Return',
        ...(this.context?.isWarrantyAvailable && { warranty: 'Warranty' }),
      },
      shippingCarriers: {},
      initialCostBoundsLoaded: false,
    }
  }

  returnType = {
    giftReturn: 'Gift Exchange',
    return: 'Return',
    draftGiftReturn: 'Gift Exchange',
    warranty: 'Warranty',
  }

  getFilterStorageKey = () => {
    return `returns-filters-${this.context?.companyId || 'default'}`
  }

  saveFiltersToStorage = (filters,tags) => {
    try {
      const filterData = {
        filters,
        tags,
        timestamp: Date.now(),
      }
      localStorage.setItem(this.getFilterStorageKey(),JSON.stringify(filterData))
    } catch (error) {
      console.warn('Failed to save filters to localStorage:',error)
    }
  }

  loadFiltersFromStorage = () => {
    try {
      const storageKey = this.getFilterStorageKey()
      const savedData = localStorage.getItem(storageKey)

      if (savedData) {
        const filterData = JSON.parse(savedData)
        const daysSinceLastUse = (Date.now() - filterData.timestamp) / (1000 * 60 * 60 * 24)

        if (daysSinceLastUse <= 30) {
          return {
            filters: filterData.filters || {},
            tags: filterData.tags || {},
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load filters from localStorage:',error)
    }

    return {
      filters: {},
      tags: {},
    }
  }

  clearPersistedFilters = () => {
    try {
      localStorage.removeItem(this.getFilterStorageKey())
    } catch (error) {
      console.warn('Failed to clear filters from localStorage:',error)
    }
  }

  resetAllFilters = () => {
    const defaultFilters = {}

    this.setState(
      {
        filters: defaultFilters,
        tags: defaultFilters,
      },
      () => {
        this.handleTags()
        this.saveFiltersToStorage(this.state.filters,this.state.tags)
        this.tabSelected()
      }
    )
  }

  componentDidMount = async () => {
    if (!this.context?.companyId) {
      setTimeout(() => {
        this.componentDidMount()
      },100)
      return
    }

    const savedFilters = this.loadFiltersFromStorage()

    this.setState({
      filters: savedFilters.filters,
      tags: savedFilters.tags,
    },() => {
      this.initializeComponent()
    })
  }

  initializeComponent = async () => {
    try {
      await this.fetchReturnsData()
      await this.getCustomColumns()

      this.handleTags()

      this.setState({ loading: false })
    } catch (err) {
      this.setState({ loading: false })
      message.error('Error loading return settings. Please try again.',4)
    }
  };

  fetchReturnsData = async () => {
    const response = await axios.post('/api/returns',{
      filters: this.state.filters,
      selectedStatus: 'All',
      pageCurrent: 1,
      pageSize: 10,
    })
    if (response.data?.status === 'error') {
      if (err?.response?.status !== 429) {
        return message.error('Error loading your returns. Please try again.')
      }
    }

    this.setState({
      ...response.data,
      returnsEmpty: false,
    })
    if (this.context.companyId) {
      this.checkIndustrySubmitted()
    }
    const info = await getAppInfo()
    this.getResolutions()
    await this.getReturnReasons()
    await this.getReturnCountries()
    await this.getShippingCarriers()
    this.setState({
      appUrl: info.appUrl,
      isProductionApp: info.isProductionApp,
      env: info.env,
    })
  };

  updateData(object) {
    this.setState({
      ...object,
      buttonResetSearchVisible: false,
      returnsCount: 1,
    })
  }

  setVisibilityOfResetSearchButton = (visibility) => {
    this.setState({
      buttonResetSearchVisible: visibility,
    })
  };

  resetSearchResults = async (pageCurrent,searchTerm = '') => {
    try {
      this.setState({
        loadingTableData: true,
      })
      const response = await axios.post('/api/returns',{
        filters: this.state.filters,
        pageCurrent,
        pageSize: 10,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        sortedBy: this.state.sortedBy,
        rangeApplied: this.state.rangeApplied,
        searchTerm,
      })
      this.setState({
        loadingTableData: false,
      })
      if (response.data?.status === 'error') {
        if (err?.response?.status !== 429) {
          return message.error('Error loading your returns. Please try again.')
        }
      }

      this.setState({
        ...response.data,
        returnsEmpty: false,
        buttonResetSearchVisible: false,
        searchValue: searchTerm,
        pageCurrent: pageCurrent,
      })
    } catch (error) {
      this.setState({
        loadingTableData: false,
      })
      if (err?.response?.status !== 429) {
        return message.error('Error loading your returns. Please try again.')
      }
    }
  };

  searchReturns = async (searchTerm) => {
    this.setState({
      isLoading: true,
    })
    if (searchTerm == null || searchTerm === '') {
      this.setState({
        isLoading: false,
      })
      return message.error('Your search term cannot be empty.',5)
    }

    const cleanedSearchTerm = searchTerm.trim()

    try {
      const response = await axios.post('/api/returns',{
        filters: this.state.filters,
        pageCurrent: 1,
        pageSize: 10,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        sortedBy: this.state.sortedBy,
        rangeApplied: this.state.rangeApplied,
        searchTerm: cleanedSearchTerm,
      })

      if (response.data?.status === 'error') {
        this.setState({
          isLoading: false,
        })
        return message.error('Error searching returns. Please try again.',5)
      }

      const responseData = response.data
      const returns = responseData.returns
      const returnsEmpty = returns.length === 0 ? true : false

      this.setState({
        ...response.data,
        returnsEmpty,
        isLoading: false,
        searchTerm: cleanedSearchTerm,
      })
    } catch (err) {
      this.setState({
        isLoading: false,
        searchTerm: cleanedSearchTerm,
      })
      return message.error('Error searching returns. Please try again.',5)
    }
  };

  setSelectedRows(selectedRowKeys,selectedRows) {
    this.setState({
      // selectedRowKeys,
      selectedRows,
    })
  }

  bulkStatusChange = async (status) => {
    const newStatus = status
    const selectedRows = this.state.selectedRows

    this.setState({
      loading: true,
    })

    try {
      const response = await axios.post('/api/returns/bulkStatus',{
        newStatus,
        selectedRows,
      })

      if (response.data?.status === 'error') {
        this.setState({
          loading: false,
        })
        return message.error(
          'Error bulk updating status. Please try again.',
          5
        )
      }

      this.setState({
        loading: false,
        ...response.data,
        buttonResetSearchVisible: true,
        selectedRows: [],
      })
      await this.resetSearchResults(this.state.pageCurrent)

      return message.success('Status updated successfully.',5)
    } catch (err) {
      this.setState({
        loading: false,
      })
      return message.error('Error bulk updating status. Please try again.',5)
    }
  }

  tabSelected = async () => {
    const cleanedSearchTerm =
      this.state.searchValue && this.state.searchValue !== ''
        ? this.state.searchValue.trim()
        : null

    const response = await axios.post('/api/returns',{
      filters: this.state.filters,
      pageCurrent: 1,
      pageSize: 10,
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      sortedBy: this.state.sortedBy,
      rangeApplied: this.state.rangeApplied,
      searchTerm: cleanedSearchTerm,
    })
    if (response.data?.status === 'error') {
      if (err?.response?.status !== 429) {
        return message.error('Error loading your returns. Please try again.')
      }
    }

    this.setState({
      ...response.data,
      returnsEmpty: false,
      buttonResetSearchVisible: false,
      pageCurrent: 1,
    })
  }

  getResolutions = () => {
    let resolutionOptions = {
      refundToOriginal: 'Refund',
      refundToExchange: 'Exchange',
      refundToCredit: 'Store credit',
      multiple: 'Multiple',
    }
    if (this.context.featureStoreWideExchange)
      resolutionOptions = {
        ...resolutionOptions,
        storeWideExchange: 'Store-Wide Exchange',
      }
    if (this.context.featureAdvancedExchanges)
      resolutionOptions = {
        ...resolutionOptions,
        advancedExchange: 'Collection Exchange',
      }
    resolutionOptions = {
      ...resolutionOptions,
      refundToCustom1: 'Custom 1',
      refundToCustom2: 'Custom 2',
      refundToCustom3: 'Custom 3',
    }
    this.setState({
      resolution: resolutionOptions,
    })
  }

  getReturnReasons = async () => {
    try {
      const response = await axios.get(
        '/api/returnSettings/returnReasons?isDashboard=true'
      )
      if (response?.data) {
        const returnReasons = {}
        response.data.forEach((reason) => {
          returnReasons[reason.reason] = reason.reason
        })
        this.setState({
          returnReasons,
        })
      }
    } catch (err) {
      return message.error('Something went wrong please try again',5)
    }
  }

  onChange = (dates,dateStrings) => {
    console.log('onChange: ',dates,dateStrings)
    this.setState(
      {
        startDate: dateStrings[0] && dayjs(dateStrings[0]),
        endDate: dateStrings[1] && dayjs(dateStrings[1]),
        rangeApplied: true,
      },
      () => {
        this.tabSelected()
      }
    )}

  handleSorter = (key) => {
    const oldDirection =
      this.state.sortedBy.key === key ? this.state.sortedBy.direction : 'ASC'
    const newDirection = oldDirection === 'ASC' ? 'DESC' : 'ASC'
    this.setState(
      {
        sortedBy: { key,direction: newDirection },
      },
      () => this.tabSelected()
    )
  }

  paginatePages = async (paginationObject) => {
    const pageCurrent = paginationObject.current
    const { pageSize } = paginationObject
    const cleanedSearchTerm =
      this.state.searchValue && this.state.searchValue !== ''
        ? this.state.searchValue.trim()
        : null

    const response = await axios.post('/api/returns',{
      filters: this.state.filters,
      pageCurrent,
      pageSize,
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      sortedBy: this.state.sortedBy,
      rangeApplied: this.state.rangeApplied,
      searchTerm: cleanedSearchTerm,
    })
    if (response.data?.status === 'error') {
      if (err?.response?.status !== 429) {
        return message.error('Error loading your returns. Please try again.')
      }
    }

    this.setState({
      ...response.data,
      returnsEmpty: false,
      pageCurrent,
    })
  }

  handleSearchChange = (e) => {
    this.setState(
      {
        searchValue: e.target.value,
      },
      () => {
        if (this.state.searchValue === '')
          this.resetSearchResults(this.state.pageCurrent)
      }
    )
  }

  handleVisibleChange = (target,flag) => {
    this.setState({ [target]: flag })
  }

  toggleExtraFilters = () => {
    this.setState(prevState => ({ showExtraFilters: !prevState.showExtraFilters }))
  }

  handleMenuClick = (e,target,tag) => {trackingVitally('rr_dashboardfilter_clicked')
    let tagsArray = []
    if (!e.target.checked) {
      tag = tag
        ? tag
        : this.state[target === 'status' ? 'allowedStatus' : target][
        e.target.name
        ] || this.returnType[e.target.name]

      tagsArray = this.state.tagsArray.filter((item) => item !== tag)
      this.setState({
        tagsArray,
      })
    }
    this.setState(
      {
        filters: {
          ...this.state.filters,
          [target]: {
            ...this.state.filters[target],
            [e.target.name]: e.target.checked,
          },
        },
        tags: {
          ...this.state.tags,
          [target]: {
            ...this.state.tags[target],
            [e.target.name]: e.target.checked,
          },
        },
      },
      () => {
        if (e.target.checked) {
          this.handleTags()
        }

        this.saveFiltersToStorage(this.state.filters,this.state.tags)

        this.tabSelected()
      }
    )
  }

  getReturnCountries = async () => {
    try {
      const response = await axios.get('/api/returns/countries')
      if (response?.data) {
        const returnCountries = {}
        response.data.forEach((country) => {
          returnCountries[country.name] = country.name
        })
        this.setState({
          returnCountries,
        })
      }
    } catch (err) {
      console.error('Error fetching return countries:',err)
    }
  }

  getShippingCarriers = async () => {
    try {
      const response = await axios.get('/api/returns/carriers')
      if (response?.data) {
        const shippingCarriers = {}
        response.data.forEach((carrier) => {
          shippingCarriers[carrier.code] = carrier.name
        })
        this.setState({
          shippingCarriers,
        })
      }
    } catch (err) {
      console.error('Error fetching shipping carriers:',err)
    }
  }

  handleCustomizeMenuClick = (e) => {trackingVitally('rr_dashboardfilter_clicked')

    this.setState({
      filters: {
        ...this.state.filters,
        customize: {
          ...this.state.filters.customize,
          [e.target.name]: e.target.checked,
        },
      },
    })
  };

  handleTags = () => {
    const {
      status,
      type,
      resolution,
      returnReasons,
      returnCountries,
      returnMethods,
      shippingCarriers
    } = this.state.tags

    const filteredStatus =
      (status &&
        Object.entries(status)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.state.allowedStatus[key])) ||
      []
    const resolutionsArray =
      (resolution &&
        Object.entries(resolution)
          ?.filter(([key,value]) => value === true)
          ?.map(
            ([key]) => this.state.resolution[key] || this.returnType[key]
          )) ||
      []
    const returnReasonsArray =
      (returnReasons &&
        Object.entries(returnReasons)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.state.returnReasons[key])) ||
      []
    const returnCountryArray =
      (returnCountries &&
        Object.entries(returnCountries)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.state.returnCountries[key])) ||
      []
    const returnMethodArray =
      (returnMethods &&
        Object.entries(returnMethods)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.state.returnMethods[key])) ||
      []
    const shippingCarrierArray =
      (shippingCarriers &&
        Object.entries(shippingCarriers)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.state.shippingCarriers[key])) ||
      []
    const typeArray =
      (type &&
        Object.entries(type)
          ?.filter(([key,value]) => value === true)
          ?.map(([key]) => this.returnType[key])) ||
      []

    this.setState({
      tagsArray: [
        ...filteredStatus,
        ...typeArray,
        ...resolutionsArray,
        ...returnReasonsArray,
        ...returnCountryArray,
        ...returnMethodArray,
        ...shippingCarrierArray,
      ],
    })
  };

  resetFilters = (target) => {
    this.setState(
      {
        filters: {
          ...this.state.filters,
          [target]: {},
        },
        tags: {
          ...this.state.tags,
          [target]: {},
        },
      },
      () => {
        this.handleTags()

        this.saveFiltersToStorage(this.state.filters,this.state.tags)

        this.tabSelected()
      }
    )
  };

  saveCustomColumns = async () => {
    const companyId = this.context.companyId

    const customColumns = Object.entries(this.state.filters.customize)
      .filter(([key,isSelected]) => isSelected)
      .map(([key]) => key)

    try {
      const response = await axios.post('/api/returns/saveCustomColumns',{
        companyId,
        customColumns,
      })

      if (response.data?.status === 'error') {
        return message.error('Error saving dashboard configuration. Please try again.')
      }

      const newCustomColumns = Object.keys(this.state.allowedColumns).reduce((acc,key) => {
        acc[key] = response.data.customColumns.includes(key)
        return acc
      },{})

      this.setState({
        customColumns: newCustomColumns
      },() => {
        message.success('Dashboard configuration saved successfully.')
      })
    } catch (error) {
      message.error('Error saving dashboard configuration. Please try again.')
    }
  };

  getCustomColumns = async () => {
    const companyId = this.context.companyId
    try {
      const response = await axios.get('/api/returns/getCustomColumns',{
        params: { companyId },
      })

      if (response.data?.status === 'error') {
        return message.error('Error loading dashboard configuration. Please try again.')
      }

      this.setState({
        filters: {
          ...this.state.filters,
          customize: response.data.customColumns
        },
        customColumns: response.data.customColumns
      })
    } catch (error) {
      message.error('Error loading dashboard configuration. Please try again.')
    }
  };

  saveCustomColumns = async () => {
    const companyId = this.context.companyId

    const customColumns = Object.entries(this.state.filters.customize)
      .filter(([key,isSelected]) => isSelected)
      .map(([key]) => key)

    try {
      const response = await axios.post('/api/returns/saveCustomColumns',{
        companyId,
        customColumns,
      })

      if (response.data?.status === 'error') {
        return message.error('Error saving dashboard configuration. Please try again.')
      }

      const newCustomColumns = Object.keys(this.state.allowedColumns).reduce((acc,key) => {
        acc[key] = response.data.customColumns.includes(key)
        return acc
      },{})

      this.setState({
        customColumns: newCustomColumns
      },() => {
        message.success('Dashboard configuration saved successfully.')
        // Refresh the table data with new column configuration
        this.tabSelected()
      })
    } catch (error) {
      message.error('Error saving dashboard configuration. Please try again.')
    }
  };

  getCustomColumns = async () => {
    const companyId = this.context.companyId
    try {
      const response = await axios.get('/api/returns/getCustomColumns',{
        params: { companyId },
      })

      if (response.data?.status === 'error') {
        return message.error('Error loading dashboard configuration. Please try again.')
      }

      this.setState({
        filters: {
          ...this.state.filters,
          customize: response.data.customColumns
        },
        customColumns: response.data.customColumns
      })
    } catch (error) {
      message.error('Error loading dashboard configuration. Please try again.')
    }
  };

  findKeyByValue = (obj,targetValue) => {
    for (const key in obj) {
      if (obj[key] === targetValue) {
        return key
      }
    }
    return null
  }

  removeTag = (e,tag) => {
    let tagKey = this.findKeyByValue(this.state.allowedStatus,tag)
    tagKey = tagKey ? tagKey : this.findKeyByValue(this.state.resolution,tag)
    tagKey = tagKey ? tagKey : this.findKeyByValue(this.state.returnReasons,tag)
    tagKey = tagKey ? tagKey : this.findKeyByValue(this.state.returnCountries,tag)
    tagKey = tagKey ? tagKey : this.findKeyByValue(this.state.returnMethods,tag)
    tagKey = tagKey ? tagKey : this.findKeyByValue(this.state.shippingCarriers,tag)

    if (tag === 'Gift Exchange') {
      tagKey = 'giftReturn'
    }
    if (tag === 'Warranty') {
      tagKey = 'warranty'
    }
    const targetObject = {
      target: {
        name: tagKey,
        checked: false,
      },
    }
    if (Object.keys(this.state.allowedStatus)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'status',tag)
    } else if (Object.keys(this.state.resolution)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'resolution',tag)
    } else if (Object.keys(this.state.returnReasons)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'returnReasons',tag)
    } else if (Object.keys(this.state.returnCountries)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'returnCountries',tag)
    } else if (Object.keys(this.state.returnMethods)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'returnMethods',tag)
    } else if (Object.keys(this.state.shippingCarriers)?.includes(tagKey)) {
      this.handleMenuClick(targetObject,'shippingCarriers',tag)
    } else if (tag === 'Gift Exchange') {
      this.handleMenuClick(targetObject,'resolution',tag)
    } else if (tag === 'Warranty') {
      this.handleMenuClick(targetObject,'resolution',tag)
    }
  }

  closeModal = () => {
    this.setState({ showModal: false })
  }

  checkIndustrySubmitted = async () => {
    try {
      const companyId = this.context.companyId
      const response = await axios.post('/api/returns/getIndustryStatus',{
        companyId,
      })

      const { storeIndustry } = response.data

      // Set the state based on storeIndustry value
      this.setState({
        hasSubmittedIndustry: storeIndustry !== null && storeIndustry !== '',
        showModal: storeIndustry === null || storeIndustry === '',
      })
    } catch (err) {
      return message.error('Unable to fetch industry submission status',5)
    }
  }

  recreatePrepaidLabel = async (returnObjects) => {
    try {
      this.setState({
        loadingRecreateLabel: true,
      })
      const objectsWithLabelError = returnObjects.filter(
        (returnObject) => returnObject.labelStatus === 'ERROR'
      )

      if (objectsWithLabelError.length === 0) {
        this.setState({
          loadingRecreateLabel: false,
        })
        return message.error(
          'No returns selected with creating label errors.',
          4
        )
      }

      const response = await axios.post('/api/returns/recreateMultipleLabels',{
        returnObjects: objectsWithLabelError,
      })

      if (response.data.status === 'error') {
        this.setState({
          loadingRecreateLabel: false,
        })

        return message.error(
          'Error while attempting to create a new label. Try again.',
          4
        )
      }
      await this.resetSearchResults(this.state.pageCurrent)

      this.setState({
        loadingRecreateLabel: false,
      })

      if (response.data.failedRecreated.length > 0) {
        const failedReturnIds = response.data.failedRecreated.map(
          (item) =>
            this.state.returns.find((r) => r.id === item.id)?.returnNumber
        )
        message.error(
          'Label creation failed for the following returns: ' +
          failedReturnIds.join(', '),
          5
        )
      }
      if (response.data.successfullyRecreated.length > 0) {
        const successReturnId = response.data.successfullyRecreated.map(
          (item) =>
            this.state.returns.find((r) => r.id === item.id)?.returnNumber
        )
        message.success(
          'Label created successfully for the following returns: ' +
          successReturnId.join(', '),
          6
        )
      }
    } catch (err) {
      this.setState({
        loadingRecreateLabel: false,
      })
      message.error(
        'Error while attempting to create a new label. Try again.',
        2
      )
    }
  }

  render() {
    const resolution = {
      ...this.state?.resolution,
      giftReturn: 'Gift',
    }
    if (this.context?.isWarrantyAvailable) {
      resolution.warranty = 'Warranty'
    }
    const { loading,selectedRows,appUrl,showModal,loadingRecreateLabel } =
      this.state
    const hasSelected = selectedRows.length > 0
    const menu = [
      {
        key: 'Shipped',
        label: (
          <span onClick={() => this.bulkStatusChange('Shipped')} key="Shipped">
            Shipped
          </span>
        ),
      },
      {
        key: 'In Transit',
        label: (
          <span onClick={() => this.bulkStatusChange('In Transit')} key="In Transit">
            In Transit
          </span>
        ),
      },
      {
        key: 'Received',
        label: (
          <span onClick={() => this.bulkStatusChange('Received')} key="Received">
            Received
          </span>
        ),
      },
      {
        key: 'Resolved',
        label: (
          <span onClick={() => this.bulkStatusChange('Resolved')} key="Resolved">
            Resolved
          </span>
        ),
      },
      {
        key: 'Rejected',
        label: (
          <span onClick={() => this.bulkStatusChange('Rejected')} key="Rejected">
            Rejected
          </span>
        ),
      },
    ]

    const restrictedColumns = ['Return','Action']
    const menuCustomize = [
      ...Object.entries(this.state.allowedColumns)
        .map(([key,value]) => ({
          key: key,
          label: <div
            style={{ width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              style={{ width: '100%' }}
              name={key}
              checked={restrictedColumns.includes(key) || (this.state?.filters?.customize && this.state.filters.customize[key])}
              onChange={this.handleCustomizeMenuClick}
              disabled={restrictedColumns.includes(key)}
            >
              {value}
            </Checkbox>
          </div>
        })),
      {
        key: 'resetCustomize',
        label: <Row type="flex" style={{ gap: 5 }} onClick={(e) => e.stopPropagation()}>
          <Button
            type="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              this.saveCustomColumns()
              this.setState({ visibleCustomize: false })
            }}
          >
            Save
          </Button>
        </Row>
      }
    ]
    const menuStatus = [
      ...Object.entries(this.state.allowedStatus || [])
        ?.filter(([key,_]) => key !== 'All')
        ?.map(([key,value]) => {
          return {
            key: key,
            label: (
              <Checkbox
                style={{ width: '100%' }}
                name={key}
                checked={
                  this.state?.filters?.status &&
                  this.state?.filters?.status[key]
                }
                onChange={(e) => this.handleMenuClick(e,'status')}
              >
                {value}
              </Checkbox>
            ),
          }
        }),
      {
        key: 'resetStatus',
        label: (
          <Button
            type="default"
            size="small"
            onClick={() => this.resetFilters('status')}
          >
            Reset
          </Button>
        ),
      },
    ]
    const menuTypes = [
      ...Object.entries(this.state.type || {})
        ?.map(([key,value]) => {
          return {
            key,
            label: (
              <Checkbox
                style={{ width: '100%' }}
                name={key}
                checked={
                  this.state?.filters?.type &&
                  this.state?.filters?.type[key]
                }
                onChange={(e) => this.handleMenuClick(e,'type')}
              >
                {value}
              </Checkbox>
            ),
          }
        }),
      {
        key: 'resetType',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('type')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]
    const menuResolutions = [
      ...Object.entries(resolution || [])
        ?.filter(([key,_]) => key !== 'All')
        ?.map(([key,value]) => {
          return {
            key,
            label: (
              <Checkbox
                style={{ width: '100%' }}
                name={key}
                checked={
                  this.state?.filters?.resolution &&
                  this.state?.filters?.resolution[key]
                }
                onChange={(e) => this.handleMenuClick(e,'resolution')}
              >
                {value}
              </Checkbox>
            ),
          }
        }),
      {
        key: 'resetResolutions',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('resolution')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]

    const menuReturnReasons = [
      ...Object.entries(this.state.returnReasons || {})
        .filter(([key]) => key !== 'All')
        .map(([key,value]) => ({
          key,
          label: (
            <Checkbox
              style={{ width: '100%' }}
              name={key}
              checked={
                this.state?.filters?.returnReasons &&
                this.state?.filters?.returnReasons[key]
              }
              onChange={(e) => this.handleMenuClick(e,'returnReasons')}
            >
              {value}
            </Checkbox>
          ),
        })),
      {
        key: 'resetReturnReasons',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('returnReasons')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]

    const menuReturnCountry = [
      ...Object.entries(this.state.returnCountries || {})
        .map(([key,value]) => ({
          key,
          label: (
            <Checkbox
              style={{ width: '100%' }}
              name={key}
              checked={
                this.state?.filters?.returnCountries &&
                this.state?.filters?.returnCountries[key]
              }
              onChange={(e) => this.handleMenuClick(e,'returnCountries')}
            >
              {value}
            </Checkbox>
          ),
        })),
      {
        key: 'resetReturnCountry',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('returnCountries')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]

    const menuReturnMethod = [
      ...Object.entries(this.state.returnMethods || {})
        .map(([key,value]) => ({
          key,
          label: (
            <Checkbox
              style={{ width: '100%' }}
              name={key}
              checked={
                this.state?.filters?.returnMethods &&
                this.state?.filters?.returnMethods[key]
              }
              onChange={e => this.handleMenuClick(e,'returnMethods')}
            >
              {value}
            </Checkbox>
          ),
        })),
      {
        key: 'resetReturnMethod',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('returnMethods')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]

    const menuShippingCarrier = [
      ...Object.entries(this.state.shippingCarriers || {})
        .map(([key,value]) => ({
          key,
          label: (
            <Checkbox
              style={{ width: '100%' }}
              name={key}
              checked={
                this.state?.filters?.shippingCarriers &&
                this.state?.filters?.shippingCarriers[key]
              }
              onChange={(e) => this.handleMenuClick(e,'shippingCarriers')}
            >
              {value}
            </Checkbox>
          ),
        })),
      {
        key: 'resetShippingCarrier',
        label: (
          <Row type="flex">
            <Button
              type="default"
              size="small"
              onClick={() => this.resetFilters('shippingCarriers')}
            >
              Reset
            </Button>
          </Row>
        ),
      },
    ]

    const portalLink = appUrl + `/return/new?utm_source=merchant_panel&c=${this.context.identifier}`

    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute',top: '50%',left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      )
    }

    return this.context.first_use ? (
      <React.Fragment>
        {this.state.isProductionApp && this.state.env != 'development' && (
          <Helmet>
            {/* <!-- Global site tag (gtag.js) - Google Ads: 368894066 --> */}
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=AW-368894066"
            ></script>
            <script type="text/javascript">
              {` 
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments)}
                  gtag('js', new Date());

                  gtag('config', 'AW-368894066');
                  gtag('event', 'conversion', {'send_to': 'AW-368894066/uigcCMf4x9UDEPLA868B'});

                  `}
            </script>
            {/* <!-- Global site tag (gtag.js) - Google Analytics --> */}
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=UA-129175672-1"
            ></script>
            <script type="text/javascript">
              {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments)}
                  gtag('js', new Date());

                  gtag('config', 'UA-129175672-1', {
                  'page_title' : 'install_screen'
                  'page_path' : '/app-installed'
                  });
                  `}
            </script>
          </Helmet>
        )}
        <Row type="flex" justify="center" align="middle" className="onboarding">
          <Col span={18}>
            <Row type="flex" justify="center" align="top">
              <Title>Welcome to RichReturns 👋</Title>
            </Row>
            <Row type="flex" justify="center" align="top">
              <Title level={3}>Your Return-Portal is ready to go.</Title>
            </Row>

            <Row type="flex" justify="center" align="top">
              <Button
                href={`/return/new?utm_source=merchant_panel&c=${this.context.identifier}`}
                target="_blank"
                rel="noopener noreferrer"
                type="primary"
              >
                View your (unstyled) Return-Portal
              </Button>
              <Divider type="vertical" />
              <CopyToClipboard text={portalLink}>
                <Button type="primary">Copy link to your Return-Portal</Button>
              </CopyToClipboard>
              <Input
                style={{ display: 'none' }}
                type="text"
                value={portalLink}
                id="portalLink"
              />
            </Row>

            <Row type="flex" justify="center" align="top">
              <Col span={12}>
                <Divider />
              </Col>
            </Row>

            <Steps direction="vertical" style={{ marginTop: 0 }}>
              <Step
                title={
                  <React.Fragment>
                    Test a return <VideoPopup videoId="dClCHuZ3_7E" />
                  </React.Fragment>
                }
                description="Create a test-order in your Shopify store with your own email address, valid shipping address and a product. Use it to run a test return through the portal."
                status="process"
              />
              <Step
                title={
                  <React.Fragment>
                    Upload a logo. <VideoPopup videoId="XIQA6x4F_2c" />
                  </React.Fragment>
                }
                description="Customize the Return-Portal to your brand under the 'Brand Settings' tab on the left side."
                status="process"
              />
              <Step
                title={
                  <React.Fragment>
                    Connect to your store <VideoPopup videoId="QkXVEoj3RzY" />
                  </React.Fragment>
                }
                description="Click the 'Copy link' button above and paste the link anywhere on your store, e.g. footer, navigation, returns policy page. Now customers can start making returns."
                status="process"
              />
              <Step
                title={
                  <React.Fragment>
                    Prepaid labels? <VideoPopup videoId="u_VowInci08" />
                  </React.Fragment>
                }
                description="We support sending Prepaid-Labels to your customers through EasyPost, Shippo and ShipStation. Go to 'Configuration / Returns' tab on the left to configure this."
                status="process"
                icon={<SmileOutlined />}
              />
              <Step
                title="Tutorials"
                description="We have step-by-step video tutorials for you on every feature under the 'Tutorials & FAQ' tab."
                icon={<VideoCameraOutlined />}
                status="process"
              />
              <Step
                title="Important"
                description="Set a business rule for yourself and your staff to NEVER delete products AND variants that are still returnable. We recommend at least 360 days. The best strategy is to simply make them unavailable on all storefronts."
                icon={<WarningOutlined style={{ color: 'red' }} />}
                status="process"
              />
            </Steps>
          </Col>
        </Row>
        <Row type="flex" justify="center" align="middle">
          <Col span={24} style={{ textAlign: 'center' }}>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/XIQA6x4F_2c?showinfo=0&autoplay=0&rel=0"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen="allowFullScreen"
            ></iframe>
          </Col>
        </Row>
        <Row
          type="flex"
          justify="center"
          align="top"
          style={{ marginTop: '20px' }}
        >
          <Button
            onClick={() => (window.location.href = '/dashboard/returns')}
            target="_blank"
            rel="noopener noreferrer"
            type="primary"
          >
            Go to my returns
          </Button>
        </Row>
        <Row type="flex" justify="center" align="top">
          <Col span={12}>
            <Divider />
          </Col>
        </Row>
        <Row type="flex" justify="center" align="top">
          <Text>
            By using our services you agree to our{' '}
            <a
              href="https://www.richreturns.io/terms-conditions?utm_source=consumer_panel"
              target="_blank"
            >
              Terms & Conditions
            </a>
            ,{' '}
            <a
              href="https://www.richreturns.io/privacy-policy?utm_source=consumer_panel"
              target="_blank"
            >
              Acceptable Use
            </a>
            ,{' '}
            <a
              href="https://www.richcommerce.co/legal/privacy-policy?utm_source=consumer_panel"
              target="_blank"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="https://www.richcommerce.co/legal/privacy/data-processing-addendum?utm_source=consumer_panel"
              target="_blank"
            >
              Data Processing Addendum
            </a>
            .
          </Text>
        </Row>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <h1 className="TextHeading TextHeading--sm u-marginBottom--md">
          Returns
        </h1>
        <div className="ContentPanel">
          <div
            className="flex-row flex-row--noMargin flex-between-xxs"
            style={{ gap: 10,marginBottom: 20 }}
          >
            <div
              className="flex-row flex-row--noMargin"
              style={{
                width: '260px',
                display: 'flex',
                gap: 10,
                flexWrap: 'nowrap',
                alignItems: 'center',
              }}
            >
              <Tooltip
                placement="top"
                title="Search by: Customer name, email, order number, (RMA) return number, tracking number, order name'"
              >
                <Search
                  className="search-field"
                  placeholder="Search return..."
                  // enterButton="Search"
                  value={this.state.searchValue}
                  onChange={this.handleSearchChange}
                  loading={this.state.isLoading}
                  onSearch={(searchTerm) => this.searchReturns(searchTerm)}
                  style={{ width: '100%',margin: '0px' }}
                  data-intercom-target="search-return-dashboard"
                />
              </Tooltip>
              <Button
                ghost
                onClick={() =>
                  this.resetSearchResults(this.state.pageCurrent,this.state.searchValue)
                }
                style={{ height: '100%' }}
              >
                <ReloadOutlined />
              </Button>
            </div>
            <div className="flex-row flex-row--noMargin" style={{ gap: 10 }}>
              {this.context.featureMultichannel ? (
                <ManualReturn
                  updateData={this.updateData.bind(this)}
                  setVisibilityOfResetSearchButton={this.setVisibilityOfResetSearchButton.bind(
                    this
                  )}
                />
              ) : null}
              {hasSelected ? (
                <React.Fragment>
                  <Row
                    type="flex"
                    style={{ height: '100%',alignItems: 'flex-end' }}
                  >
                    <Dropdown
                      menu={{ items: menu }}
                      disabled={!hasSelected}
                      className="bulkStatusChanger"
                    >
                      <Button loading={loading}>
                        Change status <DownOutlined />
                      </Button>
                    </Dropdown>
                    <div style={{ marginLeft: '10px' }}>
                      <Tooltip
                        placement="right"
                        title="This will attempt to generate a new label for the selected returns with creating label errors and send the Approved-Email including the label to the customer"
                      >
                        <Popconfirm
                          title="Create a new label and send it to the customer?"
                          onConfirm={() => {
                            this.recreatePrepaidLabel(selectedRows)
                          }}
                          okText="Yes"
                          cancelText="Cancel"
                        >
                          <Button loading={loadingRecreateLabel}>
                            Recreate labels
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    </div>
                  </Row>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {this.context?.isWarrantyAvailable && (
                    <Dropdown
                      menu={{ items: menuTypes }}
                      onOpenChange={(flag) =>
                        this.handleVisibleChange('visibleType',flag)
                      }
                      open={this.state.visibleType}
                      data-intercom-target="dropdown-type-dashboard"
                    >
                      <Button style={{ margin: '0px' }} className="filterButtons">
                        Type <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}
                  <Dropdown
                    menu={{ items: menuStatus }}
                    onOpenChange={(flag) =>
                      this.handleVisibleChange('visibleStatus',flag)
                    }
                    open={this.state.visibleStatus}
                    data-intercom-target="dropdown-status-dashboard"
                  >
                    <Button style={{ margin: '0px' }} className="filterButtons">
                      Status <DownOutlined />
                    </Button>
                  </Dropdown>
                  <Dropdown
                    menu={{ items: menuResolutions }}
                    onOpenChange={(flag) =>
                      this.handleVisibleChange('visibleReslotions',flag)
                    }
                    open={this.state.visibleReslotions}
                    data-intercom-target="dropdown-resolution-dashboard"
                    style={{ margin: '0px' }}
                  >
                    <Button style={{ margin: '0px' }} className="filterButtons">
                      Resolution <DownOutlined />
                    </Button>
                  </Dropdown>
                  <Dropdown
                    menu={{ items: menuReturnReasons }}
                    onOpenChange={(flag) =>
                      this.handleVisibleChange('visibleReturnReasons',flag)
                    }
                    open={this.state.visibleReturnReasons}
                    data-intercom-target="dropdown-reason-dashboard"
                  >
                    <Button style={{ margin: '0px' }} className="filterButtons">
                      Reason
                      <DownOutlined />
                    </Button>
                  </Dropdown>

                  <RangePicker
                    className="dashboardRangePicker"
                    data-intercom-target="start-end-date-picker-dashboard"
                    value={
                      this.state.startDate && this.state.endDate
                        ? [this.state.startDate,this.state.endDate]
                        : null
                    }
                    presets={[
                      {
                        label: 'Today',
                        value: [dayjs().startOf('day'),dayjs().endOf('day')],
                      },
                      {
                        label: 'Yesterday',
                        value: [
                          dayjs().subtract(1,'day').startOf('day'),
                          dayjs().subtract(1,'day').endOf('day'),
                        ],
                      },
                      {
                        label: 'Last Week',
                        value: [
                          dayjs().subtract(1,'week').startOf('week'),
                          dayjs().subtract(1,'week').endOf('week'),
                        ],
                      },
                      {
                        label: 'This Month',
                        value: [dayjs().startOf('month'),dayjs().endOf('month')],
                      },
                      {
                        label: 'Last Month',
                        value: [
                          dayjs().subtract(1,'month').startOf('month'),
                          dayjs().subtract(1,'month').endOf('month'),
                        ],
                      },
                      {
                        label: 'Last Quarter',
                        value: [
                          dayjs().subtract(1,'quarter').startOf('quarter'),
                          dayjs().subtract(1,'quarter').endOf('quarter'),
                        ],
                      },
                    ]}
                    onChange={this.onChange}
                    onCalendarChange={(dateArray) => {
                      const endDateSelected = dateArray[1] != null
                      if (endDateSelected) {
                        return this.setState({
                          date: undefined,
                        })
                      }

                      const startDateSelected = dateArray[0]
                      if (startDateSelected) {
                        this.setState({
                          date: dateArray[0],
                        })
                      }
                    }}
                  />
                  <Button
                    onClick={this.toggleExtraFilters}
                    className="filterButtons"
                    style={{ margin: '0px' }}
                    type={this.state.showExtraFilters ? "primary" : "default"}
                    icon={<FilterOutlined />}
                  >
                    {this.state.showExtraFilters ? <UpOutlined /> : <DownOutlined />}
                  </Button>
                </React.Fragment>
              )}
            </div>
          </div>
          {this.state.showExtraFilters && !hasSelected && (
            <div
              className="flex-row flex-row--noMargin"
              style={{
                gap: 10,
                marginBottom: 20,
                paddingTop: 10,
                borderTop: '1px solid #f0f0f0',
                justifyContent: 'flex-end',
              }}
            >

              <Dropdown
                menu={{ items: menuReturnCountry }}
                onOpenChange={(flag) =>
                  this.handleVisibleChange('visibleReturnCountry',flag)
                }
                open={this.state.visibleReturnCountry}
                data-intercom-target="dropdown-country-dashboard"
              >
                <Button style={{ margin: '0px' }} className="filterButtons">
                  Country <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={{ items: menuReturnMethod }}
                onOpenChange={(flag) =>
                  this.handleVisibleChange('visibleReturnMethod',flag)
                }
                open={this.state.visibleReturnMethod}
                data-intercom-target="dropdown-method-dashboard"
              >
                <Button style={{ margin: '0px' }} className="filterButtons">
                  Method <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={{ items: menuShippingCarrier }}
                onOpenChange={(flag) =>
                  this.handleVisibleChange('visibleShippingCarrier',flag)
                }
                open={this.state.visibleShippingCarrier}
                data-intercom-target="dropdown-carrier-dashboard"
              >
                <Button style={{ margin: '0px' }} className="filterButtons">
                  Carrier <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                menu={{ items: menuCustomize }}
                onOpenChange={(flag) =>
                  this.handleVisibleChange('visibleCustomize',flag)
                }
                visible={this.state.visibleCustomize}
              >
                <Button
                  className="filterButtons"
                  style={{ margin: '0px',backgroundColor: '#e6f4f4' }}
                >
                  Customize Columns <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          )}
          {!hasSelected && (
            <Row>
              {this.state.tagsArray?.length > 0 &&
                this.state.tagsArray.map((tag) => {
                  return (
                    <Tag
                      closable
                      onClose={(e) => this.removeTag(e,tag)}
                      visible={true}
                      key={tag}
                      style={{ marginBottom: '20px' }}
                    >
                      {tag}
                    </Tag>
                  )
                })}
            </Row>
          )}
          <React.Fragment>
            {/* <Row type="flex" justify="center" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row> */}
            {showModal && (
              <LoginModal
                visible={showModal}
                onOk={this.handleModalSubmit}
                onClose={this.closeModal}
              />
            )}
            <ReturnsTable
              searchValue={this.state.searchValue}
              searchTerm={this.searchReturns}
              returns={this.state.returns}
              returnsCount={this.state.returnsCount}
              productsOfReturns={this.state.productsOfReturns}
              selectedRowKeys={this.state.selectedRowKeys}
              resetSearchResults={this.resetSearchResults.bind(this)}
              paginatePages={this.paginatePages.bind(this)}
              setSelectedRows={this.setSelectedRows.bind(this)}
              currentPage={this.state.pageCurrent}
              updateData={this.updateData.bind(this)}
              setVisibilityOfResetSearchButton={this.setVisibilityOfResetSearchButton.bind(
                this
              )}
              handleSorter={this.handleSorter}
              loading={this.state.loadingTableData}
              customColumns={this.state.customColumns}
            />
          </React.Fragment>
        </div>
      </React.Fragment>
    )
  }
}

export default Returns
